import { API_BASE_URL, TTS_ENABLED } from '../utils/constants.js';
import { supabase } from './supabase.js';

async function getAuthHeaders() {
  if (!supabase) throw new Error('Supabase non configuré');
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Non authentifié');
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json'
  };
}

async function getOptionalAuthHeaders() {
  if (!supabase) return { 'Content-Type': 'application/json' };
  const { data: { session } } = await supabase.auth.getSession();
  return {
    ...(session ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
    'Content-Type': 'application/json'
  };
}

// Délai max côté client pour l'analyse tactique. Le Worker borne chaque provider
// (défaut 50s, configurable via ANALYZE_PROVIDER_TIMEOUT_MS) ; on laisse une marge
// pour qu'il puisse répondre une erreur 503 propre avant que le client n'abandonne.
// Au-delà, on coupe pour ne jamais laisser le spinner « Analyse tactique en
// cours... » tourner à l'infini.
const ANALYZE_TIMEOUT_MS = 120000;
const MOBILE_ANALYZE_TIMEOUT_MS = 160000;
const MOBILE_ANALYZE_SUBMIT_TIMEOUT_MS = 30000;
const ANALYZE_JOB_POLL_MS = 2500;

function isLikelyMobileClient() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || globalThis.matchMedia?.('(pointer: coarse)')?.matches === true;
}

// Filtre un SHA-256 hex (64 caractères) ; toute autre valeur => null. Évite
// d'envoyer un hash malformé au Worker (qui le rejetterait de toute façon).
function cleanVideoHash(videoHash) {
  return /^[a-f0-9]{64}$/.test(String(videoHash || '')) ? String(videoHash).toLowerCase() : null;
}

export async function analyzeFrames(frames, question, segment = null, frameTimes = null, videoKey = null, videoHash = null) {
  if (isLikelyMobileClient()) {
    return analyzeFramesAsync(frames, question, segment, frameTimes, videoKey, videoHash);
  }

  const headers = await getAuthHeaders();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), isLikelyMobileClient() ? MOBILE_ANALYZE_TIMEOUT_MS : ANALYZE_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${API_BASE_URL}/api/analyze`, {
      method: 'POST',
      headers,
      // frame_times : instants exacts des frames (pour des timestamps fiables et
      // des comparaisons temporellement espacées). On n'envoie PAS frame_previews
      // au Worker : inutile pour l'IA et coûteux en réseau.
      // video_key : identifiant R2 sûr de la vidéo déjà uploadée. Le Worker
      // vérifie l'appartenance avant d'analyser les frames.
      // video_hash : SHA-256 de la vidéo, permet au Worker d'autoriser une clé
      // partagée et de réutiliser un résultat d'analyse en cache (économie IA).
      body: JSON.stringify({ frames, question, segment, frame_times: frameTimes, video_key: videoKey, video_hash: cleanVideoHash(videoHash) }),
      signal: controller.signal
    });
  } catch (e) {
    if (e && e.name === 'AbortError') {
      throw new Error('Analyse trop longue, réessaie avec un segment plus court.');
    }
    throw new Error("Connexion au service d'analyse impossible. Vérifie ta connexion et réessaie.");
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Erreur ${res.status}`);
  }
  return res.json();
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function analyzeFramesAsync(frames, question, segment = null, frameTimes = null, videoKey = null, videoHash = null) {
  const headers = await getAuthHeaders();
  const payload = {
    frames,
    question,
    segment,
    frame_times: frameTimes,
    video_key: videoKey,
    video_hash: cleanVideoHash(videoHash)
  };

  const submitController = new AbortController();
  const submitTimer = setTimeout(() => submitController.abort(), MOBILE_ANALYZE_SUBMIT_TIMEOUT_MS);
  let submit;
  try {
    submit = await fetch(`${API_BASE_URL}/api/analyze-async`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: submitController.signal
    });
  } catch (e) {
    if (e && e.name === 'AbortError') {
      throw new Error("Envoi de l'analyse trop long. Vérifie ta connexion et réessaie.");
    }
    throw new Error("Connexion au service d'analyse impossible. Vérifie ta connexion et réessaie.");
  } finally {
    clearTimeout(submitTimer);
  }

  const submitted = await submit.json().catch(() => ({}));
  if (!submit.ok) {
    throw new Error(submitted.error || `Erreur ${submit.status}`);
  }
  const jobId = submitted.job_id;
  if (!jobId) throw new Error("Job d'analyse non créé. Réessaie.");

  const startedAt = Date.now();
  while (Date.now() - startedAt < MOBILE_ANALYZE_TIMEOUT_MS) {
    await wait(Math.max(1000, Number(submitted.poll_after_ms) || ANALYZE_JOB_POLL_MS));
    const statusRes = await fetch(`${API_BASE_URL}/api/analyze-job/${encodeURIComponent(jobId)}`, {
      method: 'GET',
      headers
    });
    const status = await statusRes.json().catch(() => ({}));
    if (!statusRes.ok) {
      throw new Error(status.error || `Erreur analyse (${statusRes.status})`);
    }
    if (status.status === 'complete') {
      return status.result;
    }
    if (status.status === 'error') {
      throw new Error(status.error || 'Analyse indisponible. Réessaie plus tard.');
    }
  }
  throw new Error('Analyse trop longue, réessaie avec un segment plus court.');
}

export async function moderateVideoFrames(frames, segment = null, frameTimes = null, videoHash = null) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/moderate-video`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ frames, segment, frame_times: frameTimes, video_hash: cleanVideoHash(videoHash) })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Erreur modération (${res.status})`);
  }
  return data;
}

export async function generateTTS(text) {
  // Synthèse vocale désactivée (VITE_TTS_ENABLED=false) : on court-circuite avant
  // tout appel réseau pour ne jamais solliciter /api/tts. Réponse structurée,
  // identique au format d'erreur de l'API, pour rester sûre si jamais appelée.
  if (!TTS_ENABLED) {
    return { error: 'Synthèse vocale désactivée.' };
  }
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/tts`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text })
  });
  const contentType = res.headers.get('Content-Type') || '';
  if (contentType.includes('audio/')) {
    return { type: 'audio', blob: await res.blob() };
  }
  // Réponse structurée : quota atteint (429) ou voix indisponible (503).
  // On ne throw plus : l'UI affiche directement le message clair renvoyé par
  // l'API, sans jamais basculer sur une voix navigateur robotique.
  const data = await res.json().catch(() => ({}));
  if (!data.error) {
    // Réponse non reconnue : on garantit un message exploitable par l'UI.
    return { error: 'Voix momentanément indisponible. Réessaie plus tard.', ...data };
  }
  return data;
}

export async function uploadVideo(file, videoHash = null) {
  const headers = await getAuthHeaders();
  const contentType = file.type && file.type.startsWith('video/') ? file.type : 'video/mp4';
  const reqHeaders = {
    'Authorization': headers['Authorization'],
    'Content-Type': contentType
  };
  // X-Video-Hash (optionnel) : si valide, le Worker range/réutilise un objet R2
  // partagé content-addressed (déduplication du stockage entre utilisateurs).
  const hash = cleanVideoHash(videoHash);
  if (hash) reqHeaders['X-Video-Hash'] = hash;
  const res = await fetch(`${API_BASE_URL}/api/upload`, {
    method: 'PUT',
    headers: reqHeaders,
    body: file
  });
  if (!res.ok) {
    let message = '';
    try {
      const err = await res.clone().json();
      message = err.error || err.message || '';
    } catch {
      try {
        message = (await res.text()).trim();
      } catch {
        message = '';
      }
    }
    throw new Error(message || `Erreur upload (${res.status})`);
  }
  return res.json();
}

export async function importVideoFromUrl(url) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/import-video-url`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ url })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Erreur import (${res.status})`);
  }
  return data;
}

export function getVideoUrl(key) {
  return `${API_BASE_URL}/api/video/${key}`;
}

export async function getAuthorizedVideoUrl(key) {
  const headers = await getOptionalAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/video-access`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ key })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Erreur accès vidéo (${res.status})`);
  }
  if (!data.url) throw new Error('URL vidéo temporaire manquante.');
  return new URL(data.url, API_BASE_URL).toString();
}
